/**
 * AdminUnmatchedInstructorRequests — filtered list of pupils who
 * tapped "Find me an instructor" but still have no instructor linked
 * after the 7-day grace window.
 *
 * Admin-only — backend gates /api/admin/unmatched-instructor-requests
 * with get_admin_user.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Compass, Loader2, AlertCircle, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function AdminUnmatchedInstructorRequests() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await axios.get(
        `${API}/api/admin/unmatched-instructor-requests`,
        { withCredentials: true },
      );
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load — please retry.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div
      data-testid="admin-unmatched-page"
      className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-12"
    >
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link
            to="/admin"
            data-testid="admin-unmatched-back"
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Back to admin hub"
          >
            <ArrowLeft className="w-4 h-4 text-slate-600" />
          </Link>
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Compass className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-extrabold text-slate-900 dark:text-white text-base leading-tight">
              Unmatched instructor requests
            </p>
            <p className="text-[11px] text-slate-500 leading-tight">
              Pupils still waiting after the 7-day grace window
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-5 space-y-3">
        {loading && (
          <div
            data-testid="admin-unmatched-loading"
            className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm flex items-center gap-2 text-sm text-slate-500"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {error && !loading && (
          <div
            data-testid="admin-unmatched-error"
            className="rounded-2xl bg-white border border-amber-200 p-4 shadow-sm"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-900">Couldn't load</p>
                <p className="text-xs text-slate-500 mt-0.5">{error}</p>
                <button
                  type="button"
                  data-testid="admin-unmatched-retry"
                  onClick={load}
                  className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-700"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <>
            <div
              data-testid="admin-unmatched-summary"
              className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm"
            >
              <div className="flex items-baseline gap-2">
                <span
                  data-testid="admin-unmatched-total-count"
                  className="text-3xl font-black text-slate-900 leading-none"
                >
                  {data.count}
                </span>
                <p className="text-sm font-extrabold text-slate-900">
                  pupils waiting
                </p>
              </div>
              <p className="text-xs text-slate-500 mt-1.5 leading-snug">
                Started ≥ {data.grace_days ?? 7} days ago · still no
                instructor linked · banner not dismissed.
              </p>
            </div>

            {data.pupils.length === 0 && (
              <div
                data-testid="admin-unmatched-empty"
                className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm text-center"
              >
                <p className="text-sm font-bold text-slate-700">
                  All caught up.
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  No pupils are currently waiting past the grace window.
                </p>
              </div>
            )}

            <ul
              data-testid="admin-unmatched-list"
              className="space-y-2"
            >
              {data.pupils.map((p) => (
                <li
                  key={p.id}
                  data-testid={`admin-unmatched-pupil-${p.id}`}
                  className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-slate-900 truncate">
                      {p.name || p.email || "Unnamed pupil"}
                    </p>
                    {p.name && (
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">
                        {p.email}
                      </p>
                    )}
                    <p className="text-[11px] text-slate-500 mt-1">
                      Started {formatDate(p.started_at)}
                    </p>
                  </div>
                  <div
                    data-testid={`admin-unmatched-pupil-days-${p.id}`}
                    className="text-right flex-shrink-0"
                  >
                    <p className="text-xl font-black text-amber-600 leading-none">
                      {p.days_waiting}
                    </p>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-1">
                      days
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </main>
    </div>
  );
}
